import { combineEpics } from 'redux-observable';
import type ApolloClient from 'apollo-client';
import { ff } from '@atlassian/jira-feature-flagging';
import { isInMentionsIndicatorExperimentCohort } from '@atlassian/jira-mentions-indicator';
import type { RouterActionPush, Location, RouterActionReplace } from '@atlassian/jira-router';
import { withKillSwitch, withSwitch } from '../common/redux-observable';
import {
	isInAddPeopleButtonOpenTMPExperimentCohort,
	isIssueTypeInContextExperimentEnabled,
} from '../feature-flags';
import type { ReduxEpic } from '../state/types';
import boardIssueMoveEpic from './board-issue-move';
import bulkOpsEpic from './bulk-ops';
import { getCanUserInviteEpic } from './can-user-invite';
import { buildAssignToMeEpic, cardAssignToMeThrottledEpic } from './card-assign-to-me';
import { cardAutoScrollOnFilterEpic } from './card-auto-scroll';
import { cardClickEpic } from './card-click';
import { cardCreateMultipleGqlEpic } from './card-create';
import { cardDeleteEpic } from './card-delete';
import { cardGroupToggleEpic } from './card-group-toggle';
import { cardListToggleEpic } from './card-list-toggle';
import { cardPostCreateEpic } from './card-post-create';
import { cardRankAfterGlobalIssueCreateEpic } from './card-rank-after-gic';
import { cardRefreshEpic } from './card-refresh';
import cardCheckStatusEpic from './card-status-check';
import cardSetStatusEpic from './card-status-set';
import {
	cardsBulkOperationsFailureEpic,
	cardsBulkOperationsSuccessEpicCmp,
} from './cards-bulk-operations';
import { cardsFlagWithCommentEpic } from './cards-flag-with-comment';
import { beforeMoveCardsEpic } from './cards-move/before';
import { cardsMoveRequestEpic } from './cards-move/request';
import { cardsNavigateEpic } from './cards-navigate';
import cardsParentSet from './cards-parent-set';
import { checkGlobalIssueCreateEpic } from './check-global-issue-create';
import { clearMentionsEpic } from './clear-mentions';
import { detailViewCloseEpic } from './detail-view/detail-view-close';
import { detailViewOpenDebouncedEpic } from './detail-view/detail-view-open-debounced';
import { detailViewUpdateEpic } from './detail-view/detail-view-update';
import { refetchEpicPanelEpic } from './epic-panel-refetch';
import boardExtensionsEpic from './extension/extension-load-epic';
import { filterValidValuesEpic } from './filter-valid-values-epic';
import { filteredCardsEpic } from './filtered-cards';
import { issueViewUpdateEpic } from './issue-view-update';
import { loadAgilityGraphqlDataEpic } from './load-agility-graphql-data';
import { loadCardCreateConfigEpic } from './load-card-create-config';
import { loadConfigDataEpic } from './load-config-data';
import { loadConfigDataEpicCmp } from './load-config-data-cmp';
import { loadCurrentUserEpic } from './load-current-user';
import { loadIssueParentsEpic } from './load-issue-parents';
import { loadMentionsEpic } from './load-mentions';
import { loadPermissionsEpic } from './load-permissions';
import { loadPlanDataEpic } from './load-plan-data';
import { oldLoadPrioritiesEpic, loadPrioritiesEpic } from './load-priorities';
import { onCloseInsightsEpic, onTogglePanelEpic } from './panel-open';
import { selectIssueParentEpic } from './parent-click';
import { postLoadCleanDataEpic } from './post-load-clean-data';
import { realtimeEventProcessEpic } from './realtime-event-process';
import { refetchVersionsEpic } from './refetch-versions';
import { refreshDataEpic } from './refresh-data';
import { refreshDataAddIssueTypeEpic } from './refresh-data-add-issue-type';
import setSelectedIssueFromUrlEpic from './selected-issue';
import { sprintCreateEpic } from './sprint-create';
import { sprintDeleteEpic } from './sprint-delete';
import { sprintMoveEpic } from './sprint-move';
import { loadWhiteboardsEntrypointExperimentEpic } from './whiteboards-entrypoint-experiment';
import { refreshBacklog } from './work/work-refresh-data-epic';
import { workloadModalSubtaskToggleEpic } from './workload-modal';

const createRootEpic = (
	isServer: boolean,
	isCompanyManaged: boolean,
	getLocation: () => Location,
	push: RouterActionPush,
	replace: RouterActionReplace,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	swagClient: ApolloClient<any>,
): ReduxEpic => {
	const withUifCmpKillSwitch = withKillSwitch(isCompanyManaged);

	const withMentionsIndicatorExperimentKillSwitch = withKillSwitch(
		isInMentionsIndicatorExperimentCohort(),
	);

	const withAddPeopleButtonOpenTMPExperimentKillSwitch = withKillSwitch(
		isInAddPeopleButtonOpenTMPExperimentCohort(),
	);

	const withIssueTypeInContextExperimentKillSwitch = withKillSwitch(
		isIssueTypeInContextExperimentEnabled,
		!isCompanyManaged,
	);

	const clientEpics: ReduxEpic[] = isServer
		? []
		: [
				issueViewUpdateEpic,
				postLoadCleanDataEpic,
				sprintCreateEpic,
				sprintDeleteEpic,
				sprintMoveEpic,
				filteredCardsEpic,
				beforeMoveCardsEpic,
				cardsMoveRequestEpic,
				cardClickEpic(getLocation, push),
				selectIssueParentEpic(getLocation, push),
				cardsFlagWithCommentEpic,
				cardsParentSet,
				cardDeleteEpic(getLocation, push),
				cardRefreshEpic,
				cardListToggleEpic,
				cardPostCreateEpic,
				cardsNavigateEpic,
				detailViewCloseEpic(getLocation, push),
				detailViewUpdateEpic,
				detailViewOpenDebouncedEpic(getLocation, push),
				filterValidValuesEpic(getLocation, push),
				setSelectedIssueFromUrlEpic(getLocation, push, isCompanyManaged),
				// This still needs to be loaded because the critical query is not loaded in CMP
				// which holds the other user data.
				loadCurrentUserEpic,
				...withMentionsIndicatorExperimentKillSwitch([loadMentionsEpic, clearMentionsEpic]),
				withSwitch(isCompanyManaged)(loadConfigDataEpicCmp, loadConfigDataEpic),
				...withUifCmpKillSwitch([
					loadPermissionsEpic,
					loadIssueParentsEpic,
					loadCardCreateConfigEpic,
					refetchEpicPanelEpic,
					cardGroupToggleEpic,
					cardRankAfterGlobalIssueCreateEpic,
					workloadModalSubtaskToggleEpic,
				]),
				boardIssueMoveEpic,
				refreshDataEpic,
				...withIssueTypeInContextExperimentKillSwitch([refreshDataAddIssueTypeEpic]),
				realtimeEventProcessEpic,
				boardExtensionsEpic,
				buildAssignToMeEpic(swagClient),
				refreshBacklog(),
				cardAssignToMeThrottledEpic,
				cardSetStatusEpic,
				cardCheckStatusEpic,
				checkGlobalIssueCreateEpic,
				cardCreateMultipleGqlEpic,
				onTogglePanelEpic(getLocation, replace),
				onCloseInsightsEpic(getLocation, replace),
				...withKillSwitch(!isCompanyManaged)([loadPermissionsEpic]),
				...(ff('priorities.per.project.backlog-contextual-menu')
					? [loadPrioritiesEpic]
					: [oldLoadPrioritiesEpic]),
				bulkOpsEpic,
				refetchVersionsEpic,
				cardsBulkOperationsFailureEpic,
				...withKillSwitch(isCompanyManaged)([cardsBulkOperationsSuccessEpicCmp]),
				cardAutoScrollOnFilterEpic,
				...withAddPeopleButtonOpenTMPExperimentKillSwitch([getCanUserInviteEpic]),
				...withKillSwitch(() => ff('sprint_planning_whiteboards_kill_switch', false))([
					loadWhiteboardsEntrypointExperimentEpic,
				]),
			];

	const loadEpic: ReduxEpic = withSwitch(isCompanyManaged)(
		loadPlanDataEpic,
		loadAgilityGraphqlDataEpic,
	);
	return combineEpics(loadEpic, ...clientEpics);
};

export default createRootEpic;
